import React, { useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { View, Text } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { Table } from '@/components/General/Table';
import Avatar from '@/components/People/Avatar';
import { Tooltip } from '@/components/General/Tooltip';
import { Query } from '@/utils/react-query/Queries';
import { useRouter } from 'expo-router';
import { ActivityIndicator } from 'react-native-paper';
import { useQueryClient } from '@tanstack/react-query';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import BibleStudyNote from '@/components/BibleStudies/BibleStudyNote';
import ActionButton from '@/components/General/ActionButton';

const formatCustomFieldValue = (value, field) => {
    if(value === undefined || value === null || value === '') return null;
    if(Array.isArray(value)){
        return value.map(v => {
            const option = field.options?.find(o => (typeof o === 'object' ? o.value : o) === v);
            return option ? (typeof option === 'object' ? option.label : option) : v;
        }).join(', ');
    }
    const option = field.options?.find(o => (typeof o === 'object' ? o.value : o) === value);
    return option ? (typeof option === 'object' ? option.label : option) : String(value);
};

const toDayString = (v) => v instanceof Date ? DateTime.fromJSDate(v).toISODate() : v;

/**
 * Session facts come from get_bible_study_sessions; note content is hydrated
 * through a separate protected read. legacy_notes is never rendered here.
 */
const BibleStudySessionsTable = ({
    smallGroupTypeID: initialSmallGroupTypeID = null,
    smallGroupID: initialSmallGroupID = null,
    startDate: initialStartDate = null,
    endDate: initialEndDate = null,
    title = "Sessions",
    description = "All sessions within the selected date range",
    showSmallGroupInfo = true,
    scrollEnabled = true,
    showFilters = true,
    roleID = null,
    scope = 'own',
}) => {
    const { theme: { colors, fonts } } = useUnistyles();
    const router = useRouter();
    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const [sortConfig, setSortConfig] = useState(null);

    const { data: smallGroupType } = Query.smallGroupTypes.byId({ id: initialSmallGroupTypeID, options: { enabled: !!initialSmallGroupTypeID } });

    const [suggestedSearch, setSuggestedSearch] = useState('');

    // Suggested teachers limited to the type's rostered role.
    const suggestedTeachersQuery = Query.people.all({
        filters: roleID ? { conditions: [{ fieldId: 'roles', operator: 'contains_any', value: [roleID] }] } : undefined,
        filterLogic: 'AND',
        searchTerm: suggestedSearch,
        options: { enabled: roleID != null }
    });

    const [filters, setFilters] = useState([
        {
            id: "teacher",
            inputType: "component",
            label: "Teacher",
            value: null
        },
        {
            id: "dateRange",
            inputType: "daterange",
            label: "Date Range",
            value: initialStartDate ? { start: initialStartDate, end: initialEndDate } : null
        },
    ]);
    const [searchText, setSearchText] = useState("");

    const teacherFilter = filters.find(f => f.id === "teacher")?.value;
    const dateRangeFilter = filters.find(f => f.id === "dateRange")?.value;

    const updateFilterValue = (filterId, value) => {
        setFilters(prev => prev.map(f => f.id === filterId ? { ...f, value } : f));
    };

    const filtersWithComponents = useMemo(() => {
        return filters.map(f => {
            if (f.id === "teacher") {
                return {
                    ...f,
                    component: (
                        <View style={styles.teacherFilterContainer}>
                            <SheetSelector
                                titleOnly
                                type="people"
                                queryResult={roleID ? suggestedTeachersQuery : undefined}
                                queryResultSearch={setSuggestedSearch}
                                title="Teacher"
                                value={f.value}
                                onChange={(selected) => updateFilterValue("teacher", selected)}
                            />
                        </View>
                    )
                };
            }
            return f;
        });
    }, [filters, roleID, suggestedTeachersQuery, setSuggestedSearch]);

    const {
        data: sessionsData,
        isLoading,
        isError,
        refetch,
        isFetching,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage
    } = Query.bibleStudies.sessions({
        scope,
        smallGroupID: initialSmallGroupID,
        startDate: toDayString(dateRangeFilter?.start || initialStartDate),
        endDate: toDayString(dateRangeFilter?.end || initialEndDate),
        teacherID: teacherFilter?.id || null,
        searchTerm: searchText || null,
        sortKey: sortConfig?.key || null,
        sortDirection: sortConfig?.direction || null,
    });

    const sessions = useMemo(() => {
        return sessionsData?.pages?.flatMap(page => page.data) || [];
    }, [sessionsData]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'ACTIVE':
                return { text: 'success', background: 'successContainer' };
            case 'COMPLETED':
                return { text: 'info', background: 'onInfo' };
            case 'CANCELED':
                return { text: 'error', background: 'errorContainer' };
            case 'PAUSED':
                return { text: 'warning', background: 'warningContainer' };
            default:
                return { text: 'text', background: 'surface' };
        }
    };

    const columns = useMemo(() => {
        const cols = [
            {
                accessor: 'session_date',
                value: 'Date',
                sortable: true,
                renderer: (row) => (
                    <Text style={styles.cellText(colors, fonts)}>
                        {formatDate(row.session_date)}
                    </Text>
                )
            },
            {
                accessor: 'sessionNote.content',
                value: 'Session Notes',
                width: 360,
                minWidth: 300,
                renderer: (row) => row.sessionNote ? (
                    <BibleStudyNote key={row.sessionNote.id} note={row.sessionNote} loggedInUser={loggedInUser} />
                ) : <Text style={styles.cellText(colors, fonts)}>{row.has_note ? 'Note restricted' : 'No session note'}</Text>
            },
            {
                accessor: 'teacher',
                value: 'Teacher',
                sortable: true,
                renderer: (row) => (
                    <View style={styles.teacherCell}>
                        <Avatar
                            profile={{
                                id: row.teacher_id,
                                avatarStorageKey: row.teacher_avatar_storage_key,
                                avatarUpdatedAt: row.teacher_avatar_updated_at
                            }}
                            size={32}
                            disableBorder
                        />
                        <Text style={styles.teacherName(colors, fonts)}>
                            {row.teacher_first_name} {row.teacher_last_name}
                        </Text>
                    </View>
                )
            },
        ];

        if (showSmallGroupInfo) {
            cols.push({
                accessor: 'small_group_name',
                value: 'Study',
                sortable: true,
                renderer: (row) => (
                    <Text style={styles.cellText(colors, fonts)}>
                        {row.small_group_name || '-'}
                    </Text>
                )
            },
            {
                accessor: 'small_group_status',
                value: 'Study Status',
                sortable: true,
                renderer: (row) => {
                    const statusStyle = getStatusStyle(row.small_group_status);
                    return (
                        <View style={styles.statusPill(colors[statusStyle.background], colors[statusStyle.text])}>
                            <Text style={styles.statusText(colors[statusStyle.text], fonts)}>
                                {row.small_group_status}
                            </Text>
                        </View>
                    );
                }
            });
        }

        cols.push(
            {
                accessor: 'students',
                value: 'Students',
                renderer: (row) => {
                    const students = row.students || [];
                    return (
                        <View style={styles.studentsCell}>
                            {students.slice(0, 3).map((student, i) => (
                                <View key={student.id} style={{ marginLeft: i > 0 ? -12 : 0 }}>
                                    <Avatar
                                        profile={{
                                            id: student.id,
                                            firstName: student.firstName,
                                            lastName: student.lastName,
                                            avatarStorageKey: student.avatarStorageKey,
                                            avatarUpdatedAt: student.avatarUpdatedAt
                                        }}
                                        size={28}
                                        disableBorder
                                    />
                                </View>
                            ))}
                            {students.length > 3 && (
                                <View style={{ marginLeft: -12 }}>
                                    <Tooltip title={students.slice(3).map(s => `${s.firstName} ${s.lastName}`).join(', ')}>
                                        <View style={styles.moreStudentsBadge(colors)}>
                                            <Text style={styles.moreStudentsText(colors, fonts)}>
                                                +{students.length - 3}
                                            </Text>
                                        </View>
                                    </Tooltip>
                                </View>
                            )}
                            {students.length === 0 && (
                                <Text style={styles.noStudentsText(colors, fonts)}>No attendance</Text>
                            )}
                        </View>
                    );
                }
            }
        );

        const sessionCustomFields = smallGroupType?.CustomFields?.filter(f => f.displayOn === 'session') || [];

        sessionCustomFields.forEach(field => {
            cols.push({
                accessor: `custom_field_${field.id}`,
                value: field.name,
                renderer: (row) => (
                    <Text style={styles.cellText(colors, fonts)}>
                        {formatCustomFieldValue(row.custom_field_values?.[field.id], field) || '-'}
                    </Text>
                )
            });
        });

        return cols;
    }, [colors, fonts, showSmallGroupInfo, smallGroupType, loggedInUser]);

    const handleFiltersChange = (newFilters) => {
        setFilters(newFilters);
    };

    const handleClearFilters = () => {
        setFilters(filters.map(f => ({ ...f, value: null })));
        setSearchText("");
    };

    if (isError) {
        return (
            <View style={styles.errorState}>
                <Text style={styles.cellText(colors, fonts)}>Could not load sessions and notes.</Text>
                <ActionButton testID="BibleStudySessionsRetry" text="Try again" onPress={() => refetch()} />
            </View>
        );
    }

    return (
        <Table
            scrollEnabled={scrollEnabled}
            title={title}
            description={description}
            isLoading={isLoading || isFetching}
            data={sessions}
            onRowClick={showSmallGroupInfo ? (session) => router.push({ pathname: `/biblestudies/${session.small_group_id}`, params: { tab: 'sessions' } }) : undefined}
            onEndReachedThreshold={0.1}
            onEndReached={() => {
                if (!isLoading && !isFetchingNextPage && hasNextPage) {
                    fetchNextPage();
                }
            }}
            columns={columns}
            extraData={[sortConfig]}
            keyExtractor={(item, index) => item.session_id ?? index}
            sortConfig={sortConfig}
            onSortChange={setSortConfig}
            filters={showFilters ? filtersWithComponents : undefined}
            onFiltersChange={showFilters ? handleFiltersChange : undefined}
            onClearFilters={showFilters ? handleClearFilters : undefined}
            onSearchChange={showFilters ? setSearchText : undefined}
            onRefresh={() => queryClient.invalidateQueries({ queryKey: ['bibleStudies', 'sessions'] })}
            ListFooterComponent={isFetchingNextPage ? (
                <View style={styles.loadingFooter}>
                    <ActivityIndicator size="small" color={colors.primary} />
                </View>
            ) : null}
        />
    );
};

const styles = StyleSheet.create(() => ({
    teacherCell: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    teacherName: (colors, fonts) => ({
        fontSize: 14,
        fontFamily: fonts.heading,
        color: colors.text,
    }),
    teacherFilterContainer: {
        gap: 8,
    },
    cellText: (colors, fonts) => ({
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.text,
    }),
    statusPill: (backgroundColor, borderColor) => ({
        paddingVertical: 3,
        paddingHorizontal: 8,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: borderColor,
        backgroundColor: backgroundColor,
        alignSelf: 'flex-start',
    }),
    statusText: (color, fonts) => ({
        fontSize: 11,
        fontFamily: fonts.bold,
        color: color,
    }),
    studentsCell: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    moreStudentsBadge: (colors) => ({
        backgroundColor: colors.primary,
        paddingVertical: 2,
        paddingHorizontal: 4,
        borderRadius: 14,
        height: 28,
        width: 28,
        alignItems: 'center',
        justifyContent: 'center',
    }),
    moreStudentsText: (colors, fonts) => ({
        color: colors.onPrimary,
        fontSize: 10,
        fontFamily: fonts.heading,
    }),
    noStudentsText: (colors, fonts) => ({
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        fontStyle: 'italic',
    }),
    errorState: { padding: 24, gap: 12, alignItems: 'flex-start' },
    loadingFooter: {
        height: 40,
        justifyContent: 'center',
        alignItems: 'center',
    },
}));

export default BibleStudySessionsTable;
